import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  hasTooltipBeenShown,
  markTooltipAsShown,
} from "../utils/tooltipTutorial";

// Module-level: survives CommandBar remounts (which happen when
// ProductSpecs opens/closes, toggling newOrderProduct in NewOrder).
let _prevCartCount = 0;

const useNewOrderTooltips = () => {
  const cartProducts = useSelector((state) => state.newOrder.cartProducts);
  const customer = useSelector((state) => state.newOrder.customer);
  const orderType = useSelector((state) => state.newOrder.orderType);

  // State-based anchors: triggers re-render when DOM element mounts/unmounts
  const [cartAnchor, setCartAnchor] = useState(null);
  const [paymentAnchor, setPaymentAnchor] = useState(null);

  // Callback refs for child components
  const cartRef = useCallback((node) => setCartAnchor(node), []);
  const paymentRef = useCallback((node) => setPaymentAnchor(node), []);

  const [showCartTooltip, setShowCartTooltip] = useState(false);
  const [showPaymentTooltip, setShowPaymentTooltip] = useState(false);

  const handleCloseCartTooltip = useCallback(() => {
    setShowCartTooltip(false);
  }, []);

  const handleClosePaymentTooltip = useCallback(() => {
    setShowPaymentTooltip(false);
  }, []);

  const itemCount = cartProducts.length;

  // Detection: set tooltip flags when a product is added
  useEffect(() => {
    if (itemCount === 0) {
      _prevCartCount = 0;
      return;
    }

    const wasAdded = itemCount > _prevCartCount;
    _prevCartCount = itemCount;

    if (!wasAdded) return;

    setShowCartTooltip(true);

    if (
      customer &&
      orderType &&
      !hasTooltipBeenShown("NEW_ORDER_PAYMENT_HINT")
    ) {
      markTooltipAsShown("NEW_ORDER_PAYMENT_HINT");
      setShowPaymentTooltip(true);
    }
  }, [itemCount, customer, orderType]);

  // Auto-dismiss cart tooltip after 3s (StrictMode-safe: cleanup + re-run = fresh timer)
  useEffect(() => {
    if (!showCartTooltip) return;
    const timer = setTimeout(() => setShowCartTooltip(false), 3000);
    return () => clearTimeout(timer);
  }, [showCartTooltip]);

  // Auto-dismiss payment tooltip after 4s
  useEffect(() => {
    if (!showPaymentTooltip) return;
    const timer = setTimeout(() => setShowPaymentTooltip(false), 4000);
    return () => clearTimeout(timer);
  }, [showPaymentTooltip]);

  return {
    cartRef,
    paymentRef,
    cartAnchor,
    paymentAnchor,
    showCartTooltip,
    showPaymentTooltip,
    handleCloseCartTooltip,
    handleClosePaymentTooltip,
  };
};

export default useNewOrderTooltips;
